import { useState, useCallback, useEffect } from 'react';
import { fetchWithAuth } from '../utils/api';

interface ClarifyingQuestion {
  id: string;
  question: string;
  options: Array<{ label: string; description: string; value: string }>;
  category: string;
  required: boolean;
}

interface IntentAnalysis {
  entrySignals: string[];
  exitSignals: string[];
  riskManagement: Record<string, unknown>;
  universe: string;
  timeframe: string;
  confidence: number;
  clarifyingQuestions: ClarifyingQuestion[];
}

interface GeneratedCode {
  code: string;
  highlightedLines: Record<number, string>;
  warnings: string[];
  suggestions: string[];
}

interface NotebookCell {
  type: 'code' | 'markdown';
  content: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  suggestions: string[];
  riskScore: number;
  complexityScore: number;
}

interface AIUsage {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  latencyMs: number;
}

interface SkillAssessment {
  skillTier: 'novice' | 'intermediate' | 'expert';
  scaffoldingLevel: number;
  assessmentDetails: Record<string, unknown>;
  tierProgression: {
    nextTier: string | null;
    progress: number;
    requirements?: Record<string, string | boolean>;
  };
}

interface AdaptiveConfig {
  showCodeEditor: boolean;
  editorMode: 'widget' | 'syntax_highlighted' | 'full_ide';
  aiMode: 'full_scaffolding' | 'co_creation' | 'autocomplete';
  metricsMode: 'basic' | 'standard' | 'forensic';
  showAdvancedOptions: boolean;
  aiProactiveSuggestions: boolean;
  askClarifyingQuestions: boolean;
  skillTier: 'novice' | 'intermediate' | 'expert';
  scaffoldingLevel: number;
}

interface NotebookContext {
  notebookPath?: string;
  workingDirectory?: string;
  filesInDirectory?: string[];
  variables?: Array<{ name: string; type: string; value: string; shape?: string }>;
}

type AssistantPhase = 'idle' | 'analyzing' | 'questioning' | 'generating' | 'validating' | 'complete' | 'error';

/**
 * Hook for managing AI strategy assistant state.
 *
 * Provides:
 * - Intent analysis with clarifying questions
 * - Code generation
 * - Code validation
 * - Session cost tracking
 * - Skill assessment
 */
export function useStrategyAssistant(getContext?: () => Promise<NotebookContext | null>) {
  const [phase, setPhase] = useState<AssistantPhase>('idle');
  const [intent, setIntent] = useState<string>('');
  const [analysis, setAnalysis] = useState<IntentAnalysis | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [generatedCode, setGeneratedCode] = useState<GeneratedCode | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [skillAssessment, setSkillAssessment] = useState<SkillAssessment | null>(null);
  const [adaptiveConfig, setAdaptiveConfig] = useState<AdaptiveConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Session cost tracking
  const [sessionUsage, setSessionUsage] = useState<AIUsage>({
    inputTokens: 0,
    outputTokens: 0,
    costUsd: 0,
    latencyMs: 0,
  });

  // Fetch adaptive config on mount
  useEffect(() => {
    fetchAdaptiveConfig();
    fetchSkillAssessment();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accumulateUsage = useCallback((usage: AIUsage) => {
    setSessionUsage((prev) => ({
      inputTokens: prev.inputTokens + usage.inputTokens,
      outputTokens: prev.outputTokens + usage.outputTokens,
      costUsd: prev.costUsd + usage.costUsd,
      latencyMs: prev.latencyMs + usage.latencyMs,
    }));
  }, []);

  const fetchAdaptiveConfig = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/v1/ai/config/adaptive');
      if (response.ok) {
        const data = await response.json();
        setAdaptiveConfig({
          showCodeEditor: data.show_code_editor,
          editorMode: data.editor_mode,
          aiMode: data.ai_mode,
          metricsMode: data.metrics_mode,
          showAdvancedOptions: data.show_advanced_options,
          aiProactiveSuggestions: data.ai_proactive_suggestions,
          askClarifyingQuestions: data.ask_clarifying_questions,
          skillTier: data.skill_tier,
          scaffoldingLevel: data.scaffolding_level,
        });
      }
    } catch (err) {
      console.error('Failed to fetch adaptive config:', err);
    }
  }, []);

  const fetchSkillAssessment = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/v1/ai/skill/assessment');
      if (response.ok) {
        const data = await response.json();
        setSkillAssessment({
          skillTier: data.skill_tier,
          scaffoldingLevel: data.scaffolding_level,
          assessmentDetails: data.assessment_details,
          tierProgression: {
            nextTier: data.tier_progression.next_tier,
            progress: data.tier_progression.progress,
            requirements: data.tier_progression.requirements,
          },
        });
      }
    } catch (err) {
      console.error('Failed to fetch skill assessment:', err);
    }
  }, []);

  const analyzeIntent = useCallback(async (userIntent: string) => {
    setIsLoading(true);
    setPhase('analyzing');
    setError(null);
    setIntent(userIntent);

    try {
      // Gather notebook context (non-blocking — skip if unavailable)
      let notebookContext: Record<string, unknown> | null = null;
      if (getContext) {
        try {
          const ctx = await getContext();
          if (ctx) {
            notebookContext = {
              notebook_path: ctx.notebookPath,
              working_directory: ctx.workingDirectory,
              files_in_directory: ctx.filesInDirectory,
              variables: ctx.variables,
            };
          }
        } catch {
          // Context gathering failed — proceed without it
        }
      }

      const response = await fetchWithAuth('/api/v1/ai/strategy/intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: userIntent,
          existing_answers: answers,
          notebook_context: notebookContext,
        }),
      });

      if (response.status === 402) {
        const data = await response.json();
        setError(data.detail || 'Insufficient balance for AI features. Please add funds.');
        setPhase('error');
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const data = await response.json();

      const analysisResult: IntentAnalysis = {
        entrySignals: data.entry_signals,
        exitSignals: data.exit_signals,
        riskManagement: data.risk_management,
        universe: data.universe,
        timeframe: data.timeframe,
        confidence: data.confidence,
        clarifyingQuestions: data.clarifying_questions.map((q: Record<string, unknown>) => ({
          id: q.id,
          question: q.question,
          options: q.options,
          category: q.category,
          required: q.required,
        })),
      };

      setAnalysis(analysisResult);
      accumulateUsage({
        inputTokens: data.usage.input_tokens,
        outputTokens: data.usage.output_tokens,
        costUsd: data.usage.cost_usd,
        latencyMs: data.usage.latency_ms,
      });

      // Move to questioning phase if there are questions, otherwise generate
      if (analysisResult.clarifyingQuestions.length > 0) {
        setPhase('questioning');
      } else {
        // No questions, proceed to generation
        await generateCode(analysisResult, answers);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
      setPhase('error');
    } finally {
      setIsLoading(false);
    }
  }, [answers, accumulateUsage]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitAnswer = useCallback((questionId: string, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  }, []);

  const generateCode = useCallback(async (
    currentAnalysis?: IntentAnalysis,
    currentAnswers?: Record<string, string>,
  ) => {
    const analysisToUse = currentAnalysis || analysis;
    const answersToUse = currentAnswers || answers;

    if (!analysisToUse) {
      setError('No analysis available');
      return;
    }

    setIsLoading(true);
    setPhase('generating');
    setError(null);

    try {
      // Gather notebook context for code generation
      let notebookContext: Record<string, unknown> | null = null;
      if (getContext) {
        try {
          const ctx = await getContext();
          if (ctx) {
            notebookContext = {
              notebook_path: ctx.notebookPath,
              working_directory: ctx.workingDirectory,
              files_in_directory: ctx.filesInDirectory,
              variables: ctx.variables,
            };
          }
        } catch { /* proceed without context */ }
      }

      const response = await fetchWithAuth('/api/v1/ai/strategy/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent,
          answers: answersToUse,
          entry_signals: analysisToUse.entrySignals,
          exit_signals: analysisToUse.exitSignals,
          risk_management: analysisToUse.riskManagement,
          universe: analysisToUse.universe,
          timeframe: analysisToUse.timeframe,
          notebook_context: notebookContext,
        }),
      });

      if (response.status === 402) {
        const data = await response.json();
        setError(data.detail || 'Insufficient balance for AI features. Please add funds.');
        setPhase('error');
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Generation failed: ${response.statusText}`);
      }

      const data = await response.json();

      setGeneratedCode({
        code: data.code,
        highlightedLines: data.highlighted_lines,
        warnings: data.warnings,
        suggestions: data.suggestions,
      });

      accumulateUsage({
        inputTokens: data.usage.input_tokens,
        outputTokens: data.usage.output_tokens,
        costUsd: data.usage.cost_usd,
        latencyMs: data.usage.latency_ms,
      });

      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
      setPhase('error');
    } finally {
      setIsLoading(false);
    }
  }, [analysis, answers, intent, accumulateUsage, getContext]);

  const validateCode = useCallback(async (code: string) => {
    setIsLoading(true);
    setPhase('validating');
    setError(null);

    try {
      const response = await fetchWithAuth('/api/v1/ai/strategy/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (response.status === 402) {
        const data = await response.json();
        setError(data.detail || 'Insufficient balance for AI features. Please add funds.');
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Validation failed: ${response.statusText}`);
      }

      const data = await response.json();

      setValidationResult({
        isValid: data.is_valid,
        errors: data.errors,
        warnings: data.warnings,
        suggestions: data.suggestions,
        riskScore: data.risk_score,
        complexityScore: data.complexity_score,
      });

      accumulateUsage({
        inputTokens: data.usage.input_tokens,
        outputTokens: data.usage.output_tokens,
        costUsd: data.usage.cost_usd,
        latencyMs: data.usage.latency_ms,
      });

      // Return to complete phase to show results
      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
      setPhase('complete'); // Still show code even if validation failed
    } finally {
      setIsLoading(false);
    }
  }, [accumulateUsage]);

  const prepareForNotebook = useCallback(async (): Promise<NotebookCell[] | null> => {
    if (!generatedCode) {
      setError('No code to prepare');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth('/api/v1/ai/strategy/prepare-notebook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: generatedCode.code,
          warnings: generatedCode.warnings || [],
          suggestions: generatedCode.suggestions || [],
        }),
      });

      if (response.status === 402) {
        const data = await response.json();
        setError(data.detail || 'Insufficient balance for AI features.');
        setIsLoading(false);
        return null;
      }

      if (!response.ok) {
        throw new Error(`Preparation failed: ${response.statusText}`);
      }

      const data = await response.json();

      accumulateUsage({
        inputTokens: data.usage.input_tokens,
        outputTokens: data.usage.output_tokens,
        costUsd: data.usage.cost_usd,
        latencyMs: data.usage.latency_ms,
      });

      return data.cells.map((c: { type: string; content: string }) => ({
        type: c.type as 'code' | 'markdown',
        content: c.content,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preparation failed');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [generatedCode, accumulateUsage]);

  const fixCode = useCallback(async () => {
    if (!generatedCode || !validationResult) {
      setError('No code or validation result to fix');
      return;
    }

    setIsLoading(true);
    setPhase('generating'); // Reuse generating phase for fixing
    setError(null);

    try {
      const response = await fetchWithAuth('/api/v1/ai/strategy/fix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: generatedCode.code,
          errors: validationResult.errors,
          warnings: validationResult.warnings,
          suggestions: validationResult.suggestions,
        }),
      });

      if (response.status === 402) {
        const data = await response.json();
        setError(data.detail || 'Insufficient balance for AI features. Please add funds.');
        setPhase('complete');
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Fix failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Update generated code with fixed version
      setGeneratedCode({
        code: data.code,
        highlightedLines: data.highlighted_lines,
        warnings: data.warnings,
        suggestions: data.suggestions,
      });

      // Clear validation result since code changed
      setValidationResult(null);

      accumulateUsage({
        inputTokens: data.usage.input_tokens,
        outputTokens: data.usage.output_tokens,
        costUsd: data.usage.cost_usd,
        latencyMs: data.usage.latency_ms,
      });

      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fix failed');
      setPhase('complete');
    } finally {
      setIsLoading(false);
    }
  }, [generatedCode, validationResult, accumulateUsage]);

  const reset = useCallback(() => {
    setPhase('idle');
    setIntent('');
    setAnalysis(null);
    setAnswers({});
    setGeneratedCode(null);
    setValidationResult(null);
    setError(null);
    // Don't reset session usage - accumulate across session
  }, []);

  const finishQuestioning = useCallback(() => {
    if (analysis) {
      generateCode(analysis, answers);
    }
  }, [analysis, answers, generateCode]);

  return {
    // State
    phase,
    intent,
    analysis,
    answers,
    generatedCode,
    validationResult,
    skillAssessment,
    adaptiveConfig,
    error,
    isLoading,
    sessionUsage,

    // Actions
    analyzeIntent,
    submitAnswer,
    finishQuestioning,
    generateCode,
    validateCode,
    fixCode,
    prepareForNotebook,
    reset,
    fetchSkillAssessment,
  };
}

export type { ClarifyingQuestion, IntentAnalysis, GeneratedCode, ValidationResult, AIUsage, SkillAssessment, AdaptiveConfig, AssistantPhase };
